import {
  createLocation as publicCreateLocation,
  bulkCreateLocation as publicBulkCreateLocation,
  getLocation as publicGetLocation,
  listLocations as publicListLocations,
  updateLocation as publicUpdateLocation,
  queryLocations as publicQueryLocations,
  typedQueryLocations as publicTypedQueryLocations,
  setDefaultLocation as publicSetDefaultLocation,
  archiveLocation as publicArchiveLocation,
  addTypeToLocation as publicAddTypeToLocation,
  removeTypeFromLocation as publicRemoveTypeFromLocation,
  addLocationType as publicAddLocationType,
  removeLocationType as publicRemoveLocationType,
} from './locations-v1-location-locations.public.js';
import { createRESTModule } from '@wix/sdk-runtime/rest-modules';
import { createEventModule } from '@wix/sdk-runtime/event-definition-modules';
import {
  BuildRESTFunction,
  MaybeContext,
  BuildEventDefinition,
} from '@wix/sdk-types';
import { HttpClient } from '@wix/sdk-types';
import { createQueryOverloadRouter } from '@wix/sdk-runtime/query-method-router';
import {
  LocationQuery,
  LocationsQueryBuilder,
  QueryLocationsOptions,
  typedQueryLocations as universalTypedQueryLocations,
} from './locations-v1-location-locations.universal.js';
import { onLocationArchiveStatus as publicOnLocationArchiveStatus } from './locations-v1-location-locations.public.js';
import { onLocationCreated as publicOnLocationCreated } from './locations-v1-location-locations.public.js';
import { onLocationSetDefaultLocation as publicOnLocationSetDefaultLocation } from './locations-v1-location-locations.public.js';
import { onLocationUpdated as publicOnLocationUpdated } from './locations-v1-location-locations.public.js';

function customQueryLocations(httpClient: HttpClient) {
  const router = createQueryOverloadRouter({
    builderQueryFunction: (options?: QueryLocationsOptions) =>
      publicQueryLocations(httpClient)(options),
    typedQueryFunction: (
      query: LocationQuery,
      options?: QueryLocationsOptions
    ) => publicTypedQueryLocations(httpClient)(query, options),
    hasOptionsParameter: true,
  });

  function overloadedQuery(
    query: LocationQuery,
    options?: QueryLocationsOptions
  ): ReturnType<typeof universalTypedQueryLocations>;
  function overloadedQuery(
    options?: QueryLocationsOptions
  ): LocationsQueryBuilder;
  function overloadedQuery(
    queryOrOptions?: LocationQuery | QueryLocationsOptions,
    options?: QueryLocationsOptions
  ): any {
    return router(...arguments);
  }

  return overloadedQuery;
}

export const createLocation: MaybeContext<
  BuildRESTFunction<typeof publicCreateLocation> & typeof publicCreateLocation
> = /*#__PURE__*/ createRESTModule(publicCreateLocation);
/** @internal */
export const bulkCreateLocation: MaybeContext<
  BuildRESTFunction<typeof publicBulkCreateLocation> &
    typeof publicBulkCreateLocation
> = /*#__PURE__*/ createRESTModule(publicBulkCreateLocation);
export const getLocation: MaybeContext<
  BuildRESTFunction<typeof publicGetLocation> & typeof publicGetLocation
> = /*#__PURE__*/ createRESTModule(publicGetLocation);
export const listLocations: MaybeContext<
  BuildRESTFunction<typeof publicListLocations> & typeof publicListLocations
> = /*#__PURE__*/ createRESTModule(publicListLocations);
export const updateLocation: MaybeContext<
  BuildRESTFunction<typeof publicUpdateLocation> & typeof publicUpdateLocation
> = /*#__PURE__*/ createRESTModule(publicUpdateLocation);
export const setDefaultLocation: MaybeContext<
  BuildRESTFunction<typeof publicSetDefaultLocation> &
    typeof publicSetDefaultLocation
> = /*#__PURE__*/ createRESTModule(publicSetDefaultLocation);
export const archiveLocation: MaybeContext<
  BuildRESTFunction<typeof publicArchiveLocation> & typeof publicArchiveLocation
> = /*#__PURE__*/ createRESTModule(publicArchiveLocation);
export const addTypeToLocation: MaybeContext<
  BuildRESTFunction<typeof publicAddTypeToLocation> &
    typeof publicAddTypeToLocation
> = /*#__PURE__*/ createRESTModule(publicAddTypeToLocation);
export const removeTypeFromLocation: MaybeContext<
  BuildRESTFunction<typeof publicRemoveTypeFromLocation> &
    typeof publicRemoveTypeFromLocation
> = /*#__PURE__*/ createRESTModule(publicRemoveTypeFromLocation);
export const addLocationType: MaybeContext<
  BuildRESTFunction<typeof publicAddLocationType> & typeof publicAddLocationType
> = /*#__PURE__*/ createRESTModule(publicAddLocationType);
export const removeLocationType: MaybeContext<
  BuildRESTFunction<typeof publicRemoveLocationType> &
    typeof publicRemoveLocationType
> = /*#__PURE__*/ createRESTModule(publicRemoveLocationType);
export const queryLocations: MaybeContext<
  BuildRESTFunction<typeof customQueryLocations> & typeof customQueryLocations
> = /*#__PURE__*/ createRESTModule(customQueryLocations);
/**
 * Triggered when a location is archived.
 */
export const onLocationArchiveStatus: BuildEventDefinition<
  typeof publicOnLocationArchiveStatus
> &
  typeof publicOnLocationArchiveStatus = createEventModule(
  publicOnLocationArchiveStatus
);
/**
 * Triggered when a location is created.
 */
export const onLocationCreated: BuildEventDefinition<
  typeof publicOnLocationCreated
> &
  typeof publicOnLocationCreated = createEventModule(publicOnLocationCreated);
/**
 * Triggered when a location is set as default.
 */
export const onLocationSetDefaultLocation: BuildEventDefinition<
  typeof publicOnLocationSetDefaultLocation
> &
  typeof publicOnLocationSetDefaultLocation = createEventModule(
  publicOnLocationSetDefaultLocation
);
/** */
export const onLocationUpdated: BuildEventDefinition<
  typeof publicOnLocationUpdated
> &
  typeof publicOnLocationUpdated = createEventModule(publicOnLocationUpdated);

export {
  LocationStatus,
  LocationType,
  DayOfWeek,
  SortOrder,
  WebhookIdentityType,
} from './locations-v1-location-locations.universal.js';
export {
  Location,
  Address,
  StreetAddress,
  AddressLocation,
  BusinessSchedule,
  TimePeriod,
  SpecialHourPeriod,
  ExtendedFields,
  CreateLocationRequest,
  CreateLocationResponse,
  BulkCreateLocationRequest,
  BulkCreateLocationResponse,
  FailedCreateLocation,
  GetLocationRequest,
  GetLocationResponse,
  ListLocationsRequest,
  Sorting,
  Paging,
  ListLocationsResponse,
  PagingMetadata,
  UpdateLocationRequest,
  UpdateLocationResponse,
  BulkUpdateLocationRequest,
  BulkUpdateLocationResponse,
  FailedUpdateLocation,
  QueryLocationsRequest,
  Query,
  QueryLocationsResponse,
  GetOrCreateDefaultLocationRequest,
  GetOrCreateDefaultLocationResponse,
  SetDefaultLocationRequest,
  SetDefaultLocationResponse,
  SetDefaultLocation,
  ArchiveLocationRequest,
  ArchiveLocationResponse,
  ArchiveLocation,
  UnarchiveLocationRequest,
  UnarchiveLocationResponse,
  AddTypeToLocationRequest,
  AddTypeToLocationResponse,
  RemoveTypeFromLocationRequest,
  RemoveTypeFromLocationResponse,
  MetasiteLocationsRequest,
  MetasiteLocationsResponse,
  AddLocationTypeRequest,
  AddLocationTypeResponse,
  RemoveLocationTypeRequest,
  RemoveLocationTypeResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  RestoreInfo,
  EntityUpdatedEvent,
  EntityDeletedEvent,
  ActionEvent,
  MessageEnvelope,
  IdentificationData,
  IdentificationDataIdOneOf,
  AccountInfo,
  BaseEventMetadata,
  EventMetadata,
  AccountInfoMetadata,
  LocationArchiveStatusEnvelope,
  LocationCreatedEnvelope,
  LocationSetDefaultLocationEnvelope,
  LocationUpdatedEnvelope,
  ListLocationsOptions,
  UpdateLocation,
  QueryLocationsOptions,
  LocationsQueryResult,
  LocationsQueryBuilder,
  LocationQuerySpec,
} from './locations-v1-location-locations.universal.js';
export { utils } from './locations-v1-location-locations.universal.js';
export {
  LocationStatusWithLiterals,
  LocationTypeWithLiterals,
  DayOfWeekWithLiterals,
  SortOrderWithLiterals,
  WebhookIdentityTypeWithLiterals,
  CreateLocationApplicationErrors,
  UpdateLocationApplicationErrors,
  SetDefaultLocationApplicationErrors,
  ArchiveLocationApplicationErrors,
  AddTypeToLocationApplicationErrors,
  RemoveTypeFromLocationApplicationErrors,
  AddLocationTypeApplicationErrors,
  RemoveLocationTypeApplicationErrors,
  CommonQueryWithEntityContext,
  LocationQuery,
} from './locations-v1-location-locations.universal.js';
